/**
 * Admin Layout
 *
 * Internal admin dashboard layout - NOT for agent-facing pages.
 * Clean, utilitarian design focused on operations.
 * Wrapped with AdminGate for access confirmation.
 * Sidebar items filtered by user role (admin sees all, sales sees only Contracts).
 */

import { ReactNode, useState } from "react";
import { NavLink } from "@/components/NavLink";
import { useLocation, Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Settings2,
  Shield,
  DollarSign,
  LogOut,
  Eye,
  FileText,
  BookOpen,
  Archive,
  ScrollText,
  Menu,
  Headphones,
  HeartPulse,
  Megaphone,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { AdminGate } from "./AdminGate";
import { useDevIntakeContext } from "@/hooks/useDevIntake";
import { DevOverridesBadge } from "./DevOverridesBadge";
import { useAdmin, AdminRole } from "@/hooks/useAdmin";
import { useAccountManager } from "@/hooks/useAccountManager";
import { useAuth } from "@/hooks/useAuth";
import { useIsMobile } from "@/hooks/use-mobile";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

interface AdminLayoutProps {
  children: ReactNode;
}

interface NavItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
  roles: AdminRole[];
}

const navItems: NavItem[] = [
  { title: "Overview", url: "/admin", icon: LayoutDashboard, roles: ["admin"] },
  { title: "Routing Inspector", url: "/admin/routing-inspector", icon: Eye, roles: ["admin"] },
  { title: "Contracts", url: "/admin/contracts", icon: FileText, roles: ["admin", "sales"] },
  { title: "Delivery Ledger", url: "/admin/delivery-ledger", icon: BookOpen, roles: ["admin"] },
  { title: "Lead Vault", url: "/admin/lead-vault", icon: Archive, roles: ["admin"] },
  { title: "Floor Pricing", url: "/admin/pricing", icon: DollarSign, roles: ["admin"] },
  { title: "Agents", url: "/admin/agents", icon: Users, roles: ["admin"] },
  { title: "Account Managers", url: "/admin/account-managers", icon: Headphones, roles: ["admin"] },
  { title: "Media Buying", url: "/admin/media-buying", icon: Megaphone, roles: ["admin"] },
  { title: "Client Health", url: "/admin/client-health", icon: HeartPulse, roles: ["admin"] },
  { title: "System", url: "/admin/system", icon: Settings2, roles: ["admin"] },
  { title: "Logs", url: "/admin/logs", icon: ScrollText, roles: ["admin"] },
];

export function AdminLayout({ children }: AdminLayoutProps) {
  const location = useLocation();
  const { role, isAccountManager: isAMRole, loading: adminLoading } = useAdmin();
  const { isAccountManager: hasAMProfile } = useAccountManager();
  const showAMPortal = isAMRole || hasAMProfile;
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useDevIntakeContext();

  const visibleNavItems = adminLoading
    ? []
    : navItems.filter((item) => item.roles.includes(role || "admin"));

  const navContent = (
    <nav className="flex flex-col gap-1 p-4">
      {visibleNavItems.map((item) => {
        const isActive = location.pathname === item.url;
        return (
          <NavLink
            key={item.url}
            to={item.url}
            onClick={() => isMobile && setSidebarOpen(false)}
            className={cn(
              "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
              "hover:bg-muted hover:text-foreground",
              isActive ? "bg-primary/10 text-primary" : "text-muted-foreground"
            )}
          >
            <item.icon className="h-4 w-4" />
            {item.title}
          </NavLink>
        );
      })}
    </nav>
  );

  return (
    <AdminGate>
      <div className="min-h-screen bg-muted/30">
        {/* Top Bar */}
        <header className="sticky top-0 z-50 w-full border-b bg-card">
          <div className={`flex h-14 items-center ${isMobile ? "px-3" : "px-6"}`}>
            <div className="flex items-center gap-3">
              {isMobile && (
                <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)} className="mr-1">
                  <Menu className="h-5 w-5" />
                </Button>
              )}
              <Shield className="h-5 w-5 text-primary" />
              <span className={`font-semibold text-foreground ${isMobile ? "text-sm" : ""}`}>
                {isMobile ? "Admin" : "Trusted James — Admin"}
              </span>
              {role === "sales" && (
                <span className="text-xs text-muted-foreground bg-accent/10 text-accent px-2 py-0.5 rounded">
                  Sales
                </span>
              )}
            </div>
            <div className="ml-auto flex items-center gap-2 md:gap-4">
              {!isMobile && <DevOverridesBadge />}
              {!isMobile && (
                <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                  Internal Use Only
                </span>
              )}
              {showAMPortal && (
                <Link
                  to="/am"
                  className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
                >
                  <Headphones className="h-3 w-3" />
                  {!isMobile && "AM Portal"}
                </Link>
              )}
              <Link
                to="/dashboard"
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
              >
                <LayoutDashboard className="h-3 w-3" />
                {!isMobile && "Agent Dashboard"}
              </Link>
              <button
                onClick={async () => {
                  await signOut();
                  navigate("/login", { replace: true });
                }}
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
              >
                <LogOut className="h-3 w-3" />
                {!isMobile && "Sign Out"}
              </button>
            </div>
          </div>
        </header>

        <div className="flex">
          {/* Desktop Sidebar */}
          {!isMobile && (
            <aside className="sticky top-14 h-[calc(100vh-3.5rem)] w-56 border-r bg-card">
              {navContent}
            </aside>
          )}

          {/* Mobile Sidebar Sheet */}
          {isMobile && (
            <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
              <SheetContent side="left" className="w-64 p-0 bg-card">
                <SheetTitle className="sr-only">Admin Navigation</SheetTitle>
                <div className="flex items-center gap-2 h-14 px-4 border-b">
                  <Shield className="h-5 w-5 text-primary" />
                  <span className="font-semibold text-foreground text-sm">Admin</span>
                </div>
                {navContent}
              </SheetContent>
            </Sheet>
          )}

          {/* Main Content */}
          <main className={`flex-1 ${isMobile ? "p-4" : "p-6"} overflow-x-hidden`}>{children}</main>
        </div>
      </div>
    </AdminGate>
  );
}

export default AdminLayout;
